<template>
  <div ref="anchorRef" class="notification-bell relative shrink-0">
    <button
      type="button"
      class="relative flex h-9 w-9 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-surface-hover hover:text-text"
      :class="{ 'bg-surface-hover text-text': open }"
      :aria-label="ariaLabel"
      :aria-expanded="open"
      aria-haspopup="dialog"
      data-testid="notification-bell"
      @click="toggle"
    >
      <i class="ri-notification-3-line text-xl" aria-hidden="true" />
      <span
        v-if="store.bellBadge"
        class="notification-bell-badge absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-semibold leading-none text-primary-foreground tabular-nums"
        data-testid="notification-bell-badge"
      >
        {{ store.bellBadge }}
      </span>
    </button>

    <Teleport to="body">
      <div
        v-if="open"
        ref="panelRef"
        class="notification-bell-panel fixed"
        :style="panelStyle"
        data-testid="notification-panel"
      >
        <NotificationDropdown @open="onOpenItem" @view-all="onViewAll" @settings="onSettings" />
      </div>
    </Teleport>
    <!-- NOTIF-008 — the "record not found / deleted" modal a dead link shows (one host, with the bell) -->
    <NotificationTargetDialog />
  </div>
</template>

<script setup lang="ts">
/**
 * NOTIF-003 — the bell in the page header (right-most): unread counter, the teleported panel,
 * polling start / stop, deep-link navigation, the one-time browser-permission prompt. A
 * notification card's click lands here too (`setNotificationPanelOpener`); opening the panel
 * dismisses the cards, and while it is open no new card shows (`setNotificationPanelState`).
 *
 * NOTIF-011 — a ready export's row does not close the panel: its click is a download, not a
 * page, and the row itself says "Downloading…" then "Download started" (the browser's own
 * download bar is the only other sign, a moment later). Every other row closes it as before.
 */
import { computed, onMounted, onUnmounted, ref, watch, type CSSProperties } from 'vue'
import { useRouter } from 'vue-router'
import NotificationDropdown from '@/components/notifications/NotificationDropdown.vue'
import NotificationTargetDialog from '@/components/notifications/NotificationTargetDialog.vue'
import {
  setNotificationOpenHandler,
  useNotificationsStore,
  type NotificationItem
} from '@/stores/notificationsStore'
import {
  dismissNotificationToasts,
  installSoundGestureHook,
  requestBrowserPermission,
  browserNotificationsSupported,
  setNotificationPanelOpener,
  setNotificationPanelState
} from '@/lib/notifications/alerts'
import {
  NOTIFICATIONS_PAGE_PATH,
  NOTIFICATION_SETTINGS_PATH,
  pageRoute
} from '@/lib/notifications/pages'
import { exportOf, exportRowState } from '@/lib/notifications/exports'
import { OVERLAY_Z_POPOVER } from '@/lib/ui/overlayLayers'

const store = useNotificationsStore()
const router = useRouter()

const open = ref(false)
const anchorRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const panelStyle = ref<CSSProperties>({})

const ariaLabel = computed(() =>
  store.unreadTotal > 0 ? `Notifications, ${store.unreadTotal} unread` : 'Notifications'
)

/** Where a notification leads: its own url, else its page, else the home. */
function targetFor(item: NotificationItem): string {
  return item.url || pageRoute(item.page_key) || '/'
}

/**
 * Follows the deep link. True once the router actually sits on it — being there already
 * counts; a cancelled navigation or one that ended elsewhere (a guard redirect) does not, and
 * the row stays unread.
 */
async function navigate(item: NotificationItem): Promise<boolean> {
  const target = router.resolve(targetFor(item)).fullPath
  try {
    await router.push(target)
  } catch {
    return false
  }
  return router.currentRoute.value.fullPath === target
}

function updatePosition() {
  const el = anchorRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  const width = Math.min(380, window.innerWidth - 16)
  const right = Math.max(8, window.innerWidth - rect.right)
  panelStyle.value = {
    top: `${Math.round(rect.bottom + 8)}px`,
    right: `${Math.round(Math.min(right, window.innerWidth - width - 8))}px`,
    zIndex: OVERLAY_Z_POPOVER
  }
}

function openPanel() {
  dismissNotificationToasts() // the cards would sit on top of the panel
  if (open.value) return
  open.value = true
  updatePosition()
  void store.openList()
  if (
    store.settings.browser_enabled &&
    browserNotificationsSupported() &&
    window.Notification.permission === 'default'
  ) {
    void requestBrowserPermission()
  }
}

function toggle() {
  if (open.value) close()
  else openPanel()
}

function close() {
  open.value = false
}

function onOpenItem(item: NotificationItem) {
  const exp = exportOf(item)
  if (!(exp && exportRowState(exp) === 'ready')) close() // a download stays in the panel
  void store.open(item)
}

function onViewAll() {
  close()
  void router.push(NOTIFICATIONS_PAGE_PATH).catch(() => {
    /* navigation cancelled / duplicated — nothing to do */
  })
}

function onSettings() {
  close()
  void router.push(NOTIFICATION_SETTINGS_PATH).catch(() => {
    /* navigation cancelled / duplicated — nothing to do */
  })
}

function onDocumentMouseDown(event: MouseEvent) {
  if (!open.value) return
  const target = event.target as Node
  if (anchorRef.value?.contains(target) || panelRef.value?.contains(target)) return
  close()
}

function onWindowKeyDown(event: KeyboardEvent) {
  if (open.value && event.key === 'Escape') {
    event.preventDefault()
    close()
  }
}

function onScrollOrResize() {
  if (open.value) updatePosition()
}

watch(
  () => router.currentRoute.value.fullPath,
  () => close()
)

onMounted(() => {
  setNotificationOpenHandler(navigate)
  setNotificationPanelOpener(openPanel)
  setNotificationPanelState(() => open.value)
  installSoundGestureHook()
  store.start()
  document.addEventListener('mousedown', onDocumentMouseDown)
  window.addEventListener('keydown', onWindowKeyDown)
  window.addEventListener('resize', onScrollOrResize)
  window.addEventListener('scroll', onScrollOrResize, true)
})

onUnmounted(() => {
  document.removeEventListener('mousedown', onDocumentMouseDown)
  window.removeEventListener('keydown', onWindowKeyDown)
  window.removeEventListener('resize', onScrollOrResize)
  window.removeEventListener('scroll', onScrollOrResize, true)
  // Logout / layout swap: the cards on screen are this user's, and nothing may land in an
  // unmounted bell — detach the module-level hooks before the store forgets everything.
  dismissNotificationToasts()
  setNotificationPanelOpener(() => {})
  setNotificationPanelState(() => false)
  setNotificationOpenHandler(() => false)
  store.stop()
})
</script>

<style scoped>
/* The counter sits on the header surface; the ring keeps it legible over the icon. */
.notification-bell-badge {
  box-shadow: 0 0 0 2px var(--color-surface);
}
</style>
